import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronLeft, CheckCircle2, XCircle, AlertCircle, Calendar, Clock, Brain, Share2, ChevronDown } from 'lucide-react';
import { Answer, LoyaltyTest } from '../types';
import { User } from '@supabase/supabase-js';
import { getUserCurrency } from '../lib/currency';
import { domToBlob } from 'modern-screenshot';
import GuessesCard from '../components/GuessesCard';
import OpenedUnlockModal from '../components/OpenedUnlockModal';
import SendFollowUp from '../components/SendFollowUp';
import ConversationInsight from '../components/ConversationInsight';
import { getReferralStatus, ReferralStatus } from '../lib/referral';

declare global { interface Window { PaystackPop: any; } }

interface TestResultsProps {
  user: User;
}

function getSpeedLabel(ms?: number): { label: string; color: string } {
  if (ms == null) return { label: '—', color: 'text-zinc-600' };
  const s = ms / 1000;
  if (s < 5)  return { label: 'Instant',  color: 'text-emerald-400' };
  if (s < 15) return { label: 'Prompt',   color: 'text-sky-400' };
  if (s < 30) return { label: 'Measured', color: 'text-amber-400' };
  return       { label: 'Hesitant', color: 'text-brand-accent' };
}

function formatTime(ms?: number): string {
  if (ms == null) return '—';
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
}

function ClockIcon3D({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" className={className}>
      <defs>
        <linearGradient id="clockRim" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#a5b4fc" />
          <stop offset="100%" stopColor="#4338ca" />
        </linearGradient>
        <radialGradient id="clockFace" cx="35%" cy="30%" r="75%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="60%" stopColor="#eef2ff" />
          <stop offset="100%" stopColor="#c7d2fe" />
        </radialGradient>
      </defs>
      <circle cx="24" cy="25.5" r="19.5" fill="#312e81" opacity="0.25" />
      <circle cx="24" cy="24" r="19.5" fill="url(#clockRim)" />
      <circle cx="24" cy="24" r="16" fill="url(#clockFace)" />
      <line x1="24" y1="9.5" x2="24" y2="12" stroke="#4338ca" strokeWidth="1.6" strokeLinecap="round" />
      <line x1="24" y1="36" x2="24" y2="38.5" stroke="#4338ca" strokeWidth="1.6" strokeLinecap="round" opacity="0.35" />
      <line x1="9.5" y1="24" x2="12" y2="24" stroke="#4338ca" strokeWidth="1.6" strokeLinecap="round" opacity="0.35" />
      <line x1="36" y1="24" x2="38.5" y2="24" stroke="#4338ca" strokeWidth="1.6" strokeLinecap="round" opacity="0.35" />
      <line x1="24" y1="24" x2="24" y2="15.5" stroke="#312e81" strokeWidth="2.4" strokeLinecap="round" />
      <line x1="24" y1="24" x2="30" y2="24" stroke="#312e81" strokeWidth="2.4" strokeLinecap="round" />
      <circle cx="24" cy="24" r="1.7" fill="#312e81" />
      <ellipse cx="18" cy="16.5" rx="6.5" ry="3.5" fill="#ffffff" opacity="0.55" />
    </svg>
  );
}

function PencilIcon3D({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" className={className}>
      <defs>
        <linearGradient id="pencilEraser" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f9a8d4" />
          <stop offset="100%" stopColor="#ec4899" />
        </linearGradient>
        <linearGradient id="pencilFerrule" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#e5e7eb" />
          <stop offset="50%" stopColor="#f9fafb" />
          <stop offset="100%" stopColor="#9ca3af" />
        </linearGradient>
        <linearGradient id="pencilBody" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#fde047" />
          <stop offset="50%" stopColor="#fef08a" />
          <stop offset="100%" stopColor="#eab308" />
        </linearGradient>
      </defs>
      <g transform="rotate(45 24 24)">
        <ellipse cx="24" cy="42" rx="5.5" ry="2" fill="#312e81" opacity="0.15" />
        <rect x="19.5" y="3" width="9" height="8" rx="2.5" fill="url(#pencilEraser)" />
        <rect x="19.5" y="10.5" width="9" height="4.5" fill="url(#pencilFerrule)" />
        <rect x="19.5" y="14.5" width="9" height="21" fill="url(#pencilBody)" />
        <rect x="22.7" y="14.5" width="1.1" height="21" fill="#ca8a04" opacity="0.5" />
        <polygon points="19.5,35.5 28.5,35.5 24,42.5" fill="#fde68a" />
        <polygon points="22.2,39.2 25.8,39.2 24,43.5" fill="#3f3f46" />
      </g>
    </svg>
  );
}

function TrendIcon3D({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" className={className}>
      <defs>
        <linearGradient id="bar1" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#86efac" />
          <stop offset="100%" stopColor="#22c55e" />
        </linearGradient>
        <linearGradient id="bar2" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6ee7b7" />
          <stop offset="100%" stopColor="#16a34a" />
        </linearGradient>
        <linearGradient id="bar3" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#5eead4" />
          <stop offset="100%" stopColor="#0d9488" />
        </linearGradient>
        <linearGradient id="arrowGrad" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%" stopColor="#fb923c" />
          <stop offset="100%" stopColor="#ea580c" />
        </linearGradient>
      </defs>
      <rect x="5" y="30" width="8" height="11" rx="2.5" fill="url(#bar1)" />
      <rect x="17" y="21" width="8" height="20" rx="2.5" fill="url(#bar2)" />
      <rect x="29" y="10" width="8" height="31" rx="2.5" fill="url(#bar3)" />
      <path
        d="M6 33 L18 18 L27 24 L41 7"
        stroke="url(#arrowGrad)"
        strokeWidth="3.4"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polygon points="41,7 33.5,8.5 39.5,14.5" fill="#ea580c" />
    </svg>
  );
}

export default function TestResults({ user }: TestResultsProps) {
  const { testId } = useParams<{ testId: string }>();
  const [test, setTest] = useState<LoyaltyTest | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [currencyCode, setCurrencyCode] = useState('GHS');
  const [currencySymbol, setCurrencySymbol] = useState('₵');
  const [paystackLoaded, setPaystackLoaded] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showGuessUnlock, setShowGuessUnlock] = useState(false);
  const [guessReferral, setGuessReferral] = useState<ReferralStatus | null>(null);
  const [shareToast, setShareToast] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [suggestedFollowUp, setSuggestedFollowUp] = useState('');
  const [suggestedFollowUpKey, setSuggestedFollowUpKey] = useState(0);
  const shareCardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    getUserCurrency().then(c => { setCurrencyCode(c.code); setCurrencySymbol(c.symbol); });
    const existing = document.getElementById('paystack-script') as HTMLScriptElement | null;
    if (existing) {
      // Script tag already exists (e.g. loaded from another page/component in
      // this session) — if it already finished loading, PaystackPop will be
      // on window; otherwise wait for its load event too.
      if (window.PaystackPop) {
        setPaystackLoaded(true);
      } else {
        existing.addEventListener('load', () => setPaystackLoaded(true));
      }
      return;
    }
    const s = document.createElement('script');
    s.id = 'paystack-script';
    s.src = 'https://js.paystack.co/v1/inline.js';
    s.onload = () => setPaystackLoaded(true);
    document.body.appendChild(s);
  }, []);

  const verifyWithServer = async (reference: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-payment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token ?? ''}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ reference }),
    });
    if (!res.ok) return { success: false };
    return res.json();
  };

  const upgradeToPremium = () => {
    if (upgrading) return;
    if (!paystackLoaded || !window.PaystackPop) {
      alert('Payment system is still loading — please try again in a moment.');
      return;
    }
    setUpgrading(true);
    // Everything from here on can throw synchronously (bad key, Paystack's
    // internal state not being ready, etc). Without a try/catch, a throw here
    // leaves "upgrading" stuck true forever and the button stuck on "Processing...".
    try {
      const handler = window.PaystackPop.setup({
        key: import.meta.env.VITE_PAYSTACK_PUBLIC_KEY,
        email: user.email,
        amount: 2000,
        currency: currencyCode,
        metadata: { user_id: user.id, feature: 'premium' },
        // NOTE: Paystack's inline.js only ever calls "callback" (on success) and
        // "onClose" (when the popup closes). onSuccess/onCancel/onFailure are
        // never invoked by the library, so this handler previously never ran.
        // IMPORTANT: this must be a plain (non-async) function. Paystack's
        // v1/inline.js checks the callback's type against `[object Function]`
        // — an `async` function is internally tagged `[object AsyncFunction]`
        // and fails that check, throwing "Attribute callback must be a valid
        // function" even though it's a real, callable function. The await
        // logic lives in an inner async IIFE instead.
        callback: (transaction: { reference: string }) => {
          (async () => {
            try {
              const result = await verifyWithServer(transaction.reference);
              if (result.success) {
                setIsPremium(true);
              }
            } finally {
              setUpgrading(false);
            }
          })();
        },
        onClose: () => setUpgrading(false),
      });
      handler.openIframe();
    } catch (err) {
      console.error('Paystack setup failed:', err);
      alert('Payment system could not start. Please refresh and try again.');
      setUpgrading(false);
    }
  };

  useEffect(() => {
    const fetchResults = async () => {
      if (!testId) return;
      try {
        const { data, error } = await supabase
          .from('tests').select('*').eq('id', testId).single();
        if (error) throw error;
        if (data && data.creator_id === user.id) {
          setTest(data);
        }

        // Check premium — same logic as Dashboard
        const { data: profile } = await supabase
          .from('profiles').select('premium_until').eq('id', user.id).maybeSingle();
        const prem = profile?.premium_until ? new Date(profile.premium_until) > new Date() : false;
        setIsPremium(prem);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching results:', error);
        setLoading(false);
      }
    };
    fetchResults();
  }, [testId, user.id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-2 border-brand-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!test) {
    return (
      <div className="text-center py-20 px-4">
        <AlertCircle className="w-12 h-12 text-brand-accent mx-auto mb-6 opacity-50" />
        <h2 className="text-xl font-light mb-2">Record Not Found</h2>
        <p className="text-zinc-500 mb-8 font-light text-sm">The requested verification record may have been expunged or restricted.</p>
        <Link to="/dashboard" className="text-brand-accent font-bold hover:underline uppercase tracking-widest text-[10px]">
          Back to Manifest
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-6 px-4">
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-2 text-zinc-600 hover:text-brand-text transition-colors mb-8 group uppercase tracking-[0.2em] text-[10px] font-bold"
      >
        <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
        Vault Return
      </Link>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        {/* Header */}
        <div className="flex flex-col gap-4 mb-8 pb-8 border-b border-brand-border">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-brand-accent mb-3">
                <CheckCircle2 className="w-4 h-4" />
                <span className="text-[10px] font-black uppercase tracking-[0.3em]">Verification Report // Secured</span>
              </div>
              <h1 className="text-3xl font-light text-brand-text leading-tight">
                {test.partner_name}
                <span className="font-serif italic text-zinc-500 ml-3">'s Response</span>
              </h1>
            </div>
            <button
              onClick={() => setShowShareModal(true)}
              className="flex items-center gap-2 bg-rose-600 px-4 py-2.5 rounded-full text-xs font-bold text-white shadow-sm active:scale-95 transition-transform shrink-0"
            >
              <Share2 className="w-3.5 h-3.5" />
              Share
            </button>
          </div>
          <div className="flex flex-col gap-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-600">
            <div className="flex items-center gap-2">
              <Calendar className="w-3 h-3 opacity-30" />
              Initiated: {new Date(test.created_at).toLocaleDateString()}
            </div>
            <div className="flex items-center gap-2 text-emerald-500/80">
              <Clock className="w-3 h-3" />
              Verified: {new Date(test.updated_at).toLocaleString()}
            </div>
          </div>
        </div>

        {/* Guesses — the count is free, the names are the unlock */}
        {testId && (
          <GuessesCard
            testId={testId}
            reloadKey={isPremium}
            onUnlock={async () => {
              const r = await getReferralStatus();
              if (r) { setGuessReferral(r); setShowGuessUnlock(true); }
              else upgradeToPremium(); // friends-unlock not set up → go straight to Premium
            }}
          />
        )}

        {/* Questions */}
        <div className="space-y-6">
          {test.questions.map((q, index) => {
            const answer = test.answers?.find((a) => a.questionId === q.id);
            const isMatch = q.expectedAnswer &&
              answer?.partnerAnswer?.toLowerCase().trim() === q.expectedAnswer.toLowerCase().trim();
            const speed = getSpeedLabel(answer?.responseTimeMs);

            return (
              <motion.div
                key={q.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-brand-card border border-brand-border rounded-2xl p-5 overflow-hidden"
              >
                {/* Item header */}
                <div className="flex flex-col gap-3 mb-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <span className="text-[10px] font-black text-brand-accent uppercase tracking-[0.3em] mb-2 block">
                        Item {String(index + 1).padStart(2, '0')}
                      </span>
                      <h3 className="text-lg font-light text-brand-text leading-relaxed">{q.text}</h3>
                    </div>
                    {q.expectedAnswer && (
                      <div className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-[0.15em] shrink-0 ${
                        isMatch
                          ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20'
                          : 'bg-brand-accent/10 text-brand-accent border border-brand-accent/20'
                      }`}>
                        {isMatch ? 'Confirmed' : 'Discrepancy'}
                      </div>
                    )}
                  </div>
                </div>

                {/* Metrics row */}
                <div className="grid grid-cols-3 gap-2 mb-5">
                  <div className="bg-brand-bg border border-brand-border rounded-xl p-3 flex flex-col gap-1">
                    <div className="flex items-center gap-1.5 text-zinc-500">
                      <span className="yhuu-icon-float" style={{ animationDelay: '0s' }}>
                        <ClockIcon3D className="w-5 h-5 shrink-0" />
                      </span>
                      <span className="text-[8px] font-black uppercase tracking-widest">Time</span>
                    </div>
                    <p className="text-brand-text font-mono text-sm font-extrabold">{formatTime(answer?.responseTimeMs)}</p>
                    <p className={`text-[9px] font-bold uppercase ${speed.color}`}>{speed.label}</p>
                  </div>

                  <div className="bg-brand-bg border border-brand-border rounded-xl p-3 flex flex-col gap-1">
                    <div className="flex items-center gap-1.5 text-zinc-500">
                      <span className="yhuu-icon-float" style={{ animationDelay: '0.4s' }}>
                        <PencilIcon3D className="w-5 h-5 shrink-0" />
                      </span>
                      <span className="text-[8px] font-black uppercase tracking-widest">Edits</span>
                    </div>
                    <p className="text-brand-text font-mono text-sm font-extrabold">
                      {answer?.editCount != null ? answer.editCount : '—'}
                    </p>
                    <p className="text-[9px] font-bold uppercase text-zinc-500">
                      {answer?.editCount != null
                        ? answer.editCount === 0 ? 'None'
                        : answer.editCount <= 3 ? 'Minimal'
                        : answer.editCount <= 10 ? 'Several'
                        : 'Heavy'
                        : ''}
                    </p>
                  </div>

                  <div className="bg-brand-bg border border-brand-border rounded-xl p-3 flex flex-col gap-1">
                    <div className="flex items-center gap-1.5 text-zinc-500">
                      <span className="yhuu-icon-float" style={{ animationDelay: '0.8s' }}>
                        <TrendIcon3D className="w-5 h-5 shrink-0" />
                      </span>
                      <span className="text-[8px] font-black uppercase tracking-widest">Length</span>
                    </div>
                    <p className="text-brand-text font-mono text-sm font-extrabold">
                      {answer?.answerLength != null ? `${answer.answerLength}c` : '—'}
                    </p>
                    <p className="text-[9px] font-bold uppercase text-zinc-500">
                      {answer?.answerLength != null
                        ? answer.answerLength < 10 ? 'Brief'
                        : answer.answerLength < 50 ? 'Concise'
                        : answer.answerLength < 150 ? 'Detailed'
                        : 'Elaborate'
                        : ''}
                    </p>
                  </div>
                </div>

                {/* Answer comparison */}
                <div className="flex flex-col gap-3 mb-4">
                  <div className={`px-5 py-4 rounded-[28px] border shadow-sm ${
                    q.expectedAnswer
                      ? 'bg-gradient-to-br from-violet-600 to-fuchsia-600 border-transparent shadow-violet-900/20'
                      : 'bg-brand-bg border-brand-border'
                  }`}>
                    <span className={`text-[10px] font-black uppercase tracking-[0.2em] block mb-2 ${
                      q.expectedAnswer ? 'text-white/70' : 'text-zinc-500'
                    }`}>
                      Internal Expectation
                    </span>
                    <div className="flex items-center gap-2 mb-2.5">
                      <div className={`h-px flex-1 ${q.expectedAnswer ? 'bg-white/30' : 'bg-zinc-200'}`} />
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        q.expectedAnswer ? 'bg-white/20' : 'bg-zinc-100'
                      }`}>
                        <ChevronDown className={`w-3 h-3 ${q.expectedAnswer ? 'text-white/80' : 'text-zinc-400'}`} />
                      </div>
                      <div className={`h-px flex-1 ${q.expectedAnswer ? 'bg-white/30' : 'bg-zinc-200'}`} />
                    </div>
                    <p className={`text-sm ${q.expectedAnswer ? 'font-extrabold text-white' : 'font-light text-zinc-500 italic'}`}>
                      {q.expectedAnswer || 'No criteria provided'}
                    </p>
                  </div>
                  <div className={`px-5 py-4 rounded-[28px] border relative shadow-sm ${
                    isMatch
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-500 border-transparent shadow-emerald-900/20'
                      : answer?.partnerAnswer || answer?.audioUrl
                      ? 'bg-gradient-to-r from-brand-accent to-pink-600 border-transparent shadow-rose-900/20'
                      : 'bg-brand-bg border-brand-border'
                  }`}>
                    <span className={`text-[10px] font-black uppercase tracking-[0.2em] block mb-2 ${
                      isMatch || answer?.partnerAnswer || answer?.audioUrl ? 'text-white/70' : 'text-zinc-500'
                    }`}>
                      External Response
                    </span>
                    <div className="flex items-center gap-2 mb-2.5">
                      <div className={`h-px flex-1 ${
                        isMatch || answer?.partnerAnswer || answer?.audioUrl ? 'bg-white/30' : 'bg-zinc-200'
                      }`} />
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        isMatch || answer?.partnerAnswer || answer?.audioUrl ? 'bg-white/20' : 'bg-zinc-100'
                      }`}>
                        <ChevronDown className={`w-3 h-3 ${
                          isMatch || answer?.partnerAnswer || answer?.audioUrl ? 'text-white/80' : 'text-zinc-400'
                        }`} />
                      </div>
                      <div className={`h-px flex-1 ${
                        isMatch || answer?.partnerAnswer || answer?.audioUrl ? 'bg-white/30' : 'bg-zinc-200'
                      }`} />
                    </div>
                    {answer?.audioUrl ? (
                      <div className="flex flex-col gap-2">
                        <audio src={answer.audioUrl} controls className="w-full" />
                        {answer?.partnerAnswer && (
                          <p className="text-sm font-extrabold text-white/90 italic">
                            {answer.partnerAnswer}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className={`text-base font-extrabold leading-relaxed ${
                        isMatch || answer?.partnerAnswer ? 'text-white' : 'text-zinc-500 italic font-light'
                      }`}>
                        {answer?.partnerAnswer || (answer?.skipped ? 'Skipped' : 'Response absent')}
                      </p>
                    )}
                    {isMatch
                      ? <CheckCircle2 className="absolute top-4 right-4 w-4 h-4 text-white/70" />
                      : q.expectedAnswer && (answer?.partnerAnswer || answer?.audioUrl)
                      ? <XCircle className="absolute top-4 right-4 w-4 h-4 text-white/60" />
                      : null}
                  </div>
                </div>

                {/* Conversation Insight — on-demand, balanced reflection rather
                    than a claim that response signals prove dishonesty. */}
                <ConversationInsight
                  testId={testId!}
                  question={q}
                  answer={answer}
                  followUpAvailable={!!test.follow_up_enabled && !test.follow_up_used}
                  onUseStarter={(text) => {
                    setSuggestedFollowUp(text);
                    setSuggestedFollowUpKey(k => k + 1);
                    // bring the follow-up box into view so the tap visibly does something
                    window.setTimeout(() => document.getElementById('follow-up-box')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 80);
                  }}
                />

                {/* Legacy AI analysis from older sessions */}
                {answer?.aiAnalysis && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 rounded-xl bg-brand-accent/5 border border-brand-accent/15"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Brain className="w-4 h-4 text-brand-accent" />
                      <span className="text-[10px] font-black uppercase tracking-[0.25em] text-brand-accent">Legacy Insight</span>
                    </div>
                    <p className="text-sm font-serif italic text-zinc-400 leading-relaxed">
                      "{typeof answer.aiAnalysis === 'string' ? answer.aiAnalysis : answer.aiAnalysis.summary}"
                    </p>
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Previous Answers (Retake Comparison) */}
        {test.previous_answers && test.previous_answers.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500">Previous Answers // Before Retake</span>
            </div>
            <div className="space-y-4">
              {test.questions.map((q, index) => {
                const prev = test.previous_answers?.find((a: any) => a.questionId === q.id);
                const curr = test.answers?.find((a) => a.questionId === q.id);
                if (!prev) return null;
                return (
                  <div key={q.id} className="bg-brand-card border border-brand-border rounded-2xl p-5">
                    <p className="text-[10px] font-black text-zinc-500 uppercase tracking-[0.2em] mb-3">
                      Item {String(index + 1).padStart(2, '0')} — {q.text}
                    </p>
                    <div className="flex flex-col gap-3">
                      <div className="px-5 py-4 rounded-[28px] border border-transparent shadow-sm bg-gradient-to-br from-violet-600 to-fuchsia-600 shadow-violet-900/20">
                        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/70 block mb-2">Before</span>
                        <div className="flex items-center gap-2 mb-2.5">
                          <div className="h-px flex-1 bg-white/30" />
                          <div className="w-5 h-5 rounded-full flex items-center justify-center bg-white/20">
                            <ChevronDown className="w-3 h-3 text-white/80" />
                          </div>
                          <div className="h-px flex-1 bg-white/30" />
                        </div>
                        {prev.audioUrl ? (
                          <audio src={prev.audioUrl} controls className="w-full" />
                        ) : (
                          <p className="text-sm font-extrabold text-white">{prev.partnerAnswer || '—'}</p>
                        )}
                      </div>
                      <div className="px-5 py-4 rounded-[28px] border border-transparent shadow-sm bg-gradient-to-br from-emerald-500 to-teal-500 shadow-emerald-900/20">
                        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/70 block mb-2">After Retake</span>
                        <div className="flex items-center gap-2 mb-2.5">
                          <div className="h-px flex-1 bg-white/30" />
                          <div className="w-5 h-5 rounded-full flex items-center justify-center bg-white/20">
                            <ChevronDown className="w-3 h-3 text-white/80" />
                          </div>
                          <div className="h-px flex-1 bg-white/30" />
                        </div>
                        {curr?.audioUrl ? (
                          <audio src={curr.audioUrl} controls className="w-full" />
                        ) : (
                          <p className="text-sm font-extrabold text-white">{curr?.partnerAnswer || '—'}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-12 p-8 rounded-2xl bg-brand-card border border-brand-border text-center">
          <p className="font-serif italic text-zinc-500 text-base leading-relaxed">
            "Clarity is the bridge to authentic connection. Proceed with empathy and wisdom."
          </p>
          <div className="mt-4 text-[10px] font-black uppercase tracking-[0.5em] text-zinc-400">
            End of Record // Yhuu-102
          </div>
        </div>

        {/* Retake info for sender */}
        {(test.retake_count ?? 0) > 0 && (
          <div className="mt-6 p-5 rounded-2xl bg-brand-card border border-brand-accent/20">
            <p className="text-[10px] font-black uppercase tracking-widest text-brand-accent mb-1">
              Recipient Retook · {test.retake_count}x
            </p>
            <p className="text-xs text-zinc-500">
              They answered again. Previous answers are saved above.
            </p>
          </div>
        )}

        {/* Follow-up question — only shows if the recipient opted in and
            the sender hasn't used their one shot yet. */}
        <SendFollowUp
          test={test}
          isPremium={isPremium}
          onSent={updated => setTest(updated)}
          onUpgrade={upgradeToPremium}
          initialQuestion={suggestedFollowUp}
          initialQuestionKey={suggestedFollowUpKey}
        />

      </motion.div>

      {/* ── SENDER SHARE CARD ── */}
      <AnimatePresence>
        {shareToast && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed top-6 left-1/2 -translate-x-1/2 bg-white text-zinc-900 px-5 py-3 rounded-full text-xs font-bold shadow-xl z-[60]"
          >
            {shareToast}
          </motion.div>
        )}
      </AnimatePresence>

      {showGuessUnlock && guessReferral && (
          <OpenedUnlockModal
            referral={guessReferral}
            title="Who do they think you are?"
            headline="Share Yhuu with 3 friends, see who they think you are"
            note="The free unlock covers sessions you send from now on. Premium unlocks this one too."
            onClose={() => setShowGuessUnlock(false)}
            onUpgrade={() => { setShowGuessUnlock(false); upgradeToPremium(); }}
            premiumProcessing={upgrading}
            currencySymbol={currencySymbol}
          />
      )}


      <AnimatePresence>
        {showShareModal && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            className="fixed inset-0 z-[55] flex flex-col"
            style={{ background: 'linear-gradient(160deg, #7c3aed 0%, #db2777 45%, #f43f5e 100%)' }}
          >
            {/* Nav */}
            <div className="flex items-center justify-between px-5 pt-10 pb-3 flex-shrink-0">
              <button
                onClick={() => setShowShareModal(false)}
                className="flex items-center gap-1.5 bg-white/75 backdrop-blur-md px-4 py-2.5 rounded-full text-sm font-bold text-rose-700 shadow-sm"
              >
                ← Back
              </button>
              <button
                onClick={async () => {
                  if (!shareCardRef.current) return;
                  setIsCapturing(true);
                  // Let the card expand to its natural (unscrolled) height before
                  // capturing — html2canvas doesn't respect overflow:auto clipping,
                  // so capturing while the card is fixed-height + scrollable produces
                  // a broken/overlapping image. Waiting a frame lets React re-render
                  // with the expanded layout first.
                  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
                  try {
                    const blob = await domToBlob(shareCardRef.current, {
                      scale: 2,
                      backgroundColor: '#ffffff',
                    });
                    if (!blob) throw new Error('Failed to generate image');

                    const fileName = `yhuu-${test.partner_name.replace(/\s+/g, '-').toLowerCase()}.png`;
                    const file = new File([blob], fileName, { type: 'image/png' });

                    if (navigator.canShare && navigator.canShare({ files: [file] })) {
                      await navigator.share({
                        files: [file],
                        title: 'Yhuu',
                        text: `I sent ${test.partner_name} anonymous questions on Yhuu 👀`,
                      }).catch(() => {});
                    } else {
                      const objectUrl = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = objectUrl;
                      link.download = fileName;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      URL.revokeObjectURL(objectUrl);
                      setShareToast('Image downloaded!');
                      setTimeout(() => setShareToast(''), 2500);
                    }
                  } catch (e) {
                    console.error('Share error', e);
                    setShareToast('Something went wrong');
                    setTimeout(() => setShareToast(''), 2500);
                  } finally {
                    setIsCapturing(false);
                  }
                }}
                disabled={isCapturing}
                className="flex items-center gap-2 bg-rose-600 px-5 py-2.5 rounded-full text-sm font-bold text-white shadow-md active:scale-95 transition-transform disabled:opacity-60"
              >
                {isCapturing ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : 'Share ↗'}
              </button>
            </div>

            {/* Privacy reminder */}
            <div className="px-5 pb-3 flex-shrink-0">
              <div className="max-w-sm mx-auto bg-white/15 backdrop-blur-md border border-white/25 rounded-2xl px-4 py-2.5 flex items-center gap-2">
                <span className="text-sm shrink-0">⚠️</span>
                <p className="text-white/90 text-[11px] font-semibold leading-snug">
                  This shows {test.partner_name}'s real answers. Only share it somewhere they'd be okay with.
                </p>
              </div>
            </div>

            {/* Card fills the rest */}
            <div className={`${isCapturing ? 'overflow-y-auto' : 'flex-1 min-h-0'} px-5 pb-6`}>
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.06, type: 'spring', damping: 24 }}
                ref={shareCardRef}
                className={`${isCapturing ? 'h-auto' : 'h-full'} w-full max-w-sm mx-auto bg-[#fce8eb] rounded-[28px] shadow-2xl flex flex-col overflow-hidden`}
              >
                {/* Branded header band */}
                <div className="flex-shrink-0 px-6 pt-6 pb-5" style={{ background: 'linear-gradient(160deg, #7c3aed 0%, #db2777 45%, #f43f5e 100%)' }}>
                  <div className="flex items-center gap-3 mb-4">
                    <img src="/brand/logo-icon.png" alt="Yhuu" className="w-12 h-12 rounded-xl shadow-sm" />
                    <span className="text-white font-black text-2xl tracking-wide">YHUU</span>
                  </div>
                  <span className="inline-block bg-white/20 border border-white/30 rounded-full px-3 py-1.5 text-[10px] font-black tracking-[0.18em] text-white uppercase">
                    They Answered Me 💌
                  </span>
                </div>

                {/* Q&A pairs — scrollable, fills space */}
                <div
                  className={`${isCapturing ? 'overflow-visible' : 'flex-1 overflow-y-auto'} px-6`}
                  style={{
                    backgroundImage: 'radial-gradient(rgba(225,29,72,0.08) 1px, transparent 1px)',
                    backgroundSize: '18px 18px',
                  }}
                >
                  {test.questions.map((q, i) => {
                    const ans = test.answers?.find(a => a.questionId === q.id);
                    return (
                      <div key={q.id}>
                        <div className="py-5">
                          <p className="text-base font-bold mb-3 leading-snug text-rose-600">
                            Q{i + 1}: {q.text}
                          </p>
                          <div className="w-full bg-rose-50 rounded-2xl px-4 py-4">
                            <p className="text-base font-bold text-gray-900 leading-snug">
                              {ans?.partnerAnswer ?? '...'}
                            </p>
                          </div>
                        </div>
                        {i < test.questions.length - 1 && (
                          <div className="h-px bg-rose-200/70 w-full" />
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-rose-200/60 flex-shrink-0">
                  <span className="text-xs font-bold text-rose-400">yhuu.life</span>
                  <span className="text-xs font-bold text-rose-600">Send yours →</span>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
